import React, { useEffect, useMemo, useRef, useState } from "react";
import "./JunebaseRobot.css";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import { Model } from "./JunebaseRobot3D";

function useSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const update = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return [ref, size];
}

export default function JunebaseRobot() {
  const [wrapRef, { w }] = useSize();

  // Responsive camera + model framing
  const { camZ, scale, y, yOffset } = useMemo(() => {
    const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
    const ww = w || 1200;

    const s = clamp(5.0 + (ww - 360) * 0.0030, 5.0, 7.8);
    const z = clamp(6.6 + (ww - 360) * 0.0030, 6.6, 9.2);
    const yy = clamp(-1.15 + (ww - 360) * 0.00035, -1.15, -0.75);
    const yo = clamp(-0.22 + (ww - 360) * 0.00035, -0.22, -0.50);

    return { camZ: z, scale: s, y: yy, yOffset: yo };
  }, [w]);

  return (
    <div ref={wrapRef} className="robot-wrapper">
      <div className="canvas-container">
        <Canvas
          camera={{ position: [0, 0, camZ], fov: 26 }}
          dpr={[1, 2]}
          gl={{ toneMappingExposure: 1.0 }}
        >
          <Environment preset="studio" environmentIntensity={0.05} />
          <ambientLight intensity={0.25} />
          <directionalLight position={[6, 8, 6]} intensity={1.2} />
          <directionalLight position={[-6, 4, -6]} intensity={0.6} />

          <Model scale={[scale, scale, scale]} position={[0, y + yOffset, 0]} />

          <OrbitControls
            enablePan={false}
            enableZoom={false}
            enableRotate={true}
            minPolarAngle={Math.PI / 2}
            maxPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </div>
    </div>
  );
}